import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { URLS } from '@shared-config/urls';
import {
    Conversation,
    FilePromptConfig,
    PromptConfig,
} from '@shared-interfaces/shared-genAi-api';
import { BasicProcessRepsonse, PromptResponse } from '@shared-interfaces/shared-process';
import { formatFilename } from '@shared-utils/helper/helper-functions';
import { Observable } from 'rxjs';
import { AnalysisResultResponse, ContractAnalysisResult } from "@shared-interfaces/shared-workflow";

// Add interfaces for prompt execution
export interface PromptExecutionRequest {
    promptIdentifier: string;
    contentCollectionId: string;
}

export interface PromptExecutionResponse {
    promptBasic: {
        promptIdentifier: string;
        name: string;
        rank: number;
    };
    promptResults: {
        promptResult: string;
        file: string;
    }[];
}

@Injectable({
    providedIn: 'root',
})
export class GenAiService {
    private httpClient = inject(HttpClient);

    askGenAi(
        promptConfiguration: PromptConfig | FilePromptConfig,
        processDetails: string
    ): Observable<BasicProcessRepsonse> {
        const conversation: Conversation =
        {
            promptConfiguration: promptConfiguration,
            addMessageHistory: false,
            parameter: {
                temperature: 0.1,
                top_p: 0.95,
                top_k: 40,
                candidate_count: 1,
            },
        }
        return this.httpClient.post<BasicProcessRepsonse>(URLS.GENAI, conversation, {
            params: {
                'process_details': processDetails
            }
        });
    }

    public analyseContract(workflowId?: string, useAlternatePrompt: boolean = false): Observable<BasicProcessRepsonse> {
        return this.httpClient.post<BasicProcessRepsonse>(
            `${URLS.EXECUTION}/analyse_contract`, {
            uid: workflowId, useAlternatePrompt
        }
        )
    }

    public analyseContractV2(workflowId?: string, promptSetId?: string, mode: 'resume' | 'full' = 'resume'): Observable<BasicProcessRepsonse> {
        return this.httpClient.post<BasicProcessRepsonse>(
            `${URLS.EXECUTION}/analyse_contract_v2`, {
            uid: workflowId,
            promptSetId: promptSetId,
            mode: mode
        }
        )
    }

    testPrompt(
        prompt: string, context: string, workflowId?: string
    ): Observable<PromptResponse> {
        return this.httpClient.post<PromptResponse>(
            `${URLS.EXECUTION}/prompt_testing`,
            {
                prompt: prompt, context: context, content_collection_id: workflowId
            }
        )
    }

    executePrompt(request: PromptExecutionRequest): Observable<PromptExecutionResponse> {
        return this.httpClient.post<PromptExecutionResponse>(
            `${URLS.FLOW}/execution/prompt_execution`,
            request
        );
    }

    getAnalysisResult(contentCollectionId: string): Observable<AnalysisResultResponse> {
        return this.httpClient.post<AnalysisResultResponse>(
            `${URLS.EXECUTION}/get_analysis_result`,
            { content_collection_id: contentCollectionId }
        );
    }
}
